import { createAction } from "@reduxjs/toolkit";
import {
  INVALIDATE_LIVE_VIEW_DATA,
} from "./actionTypes";

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import axios from "axios";
import buildUrl from "build-url-ts";
import { FETCH, CHAT_USERS, CHAT_USER, CHAT_USER_NUDGES, CHAT_USER_LOCATIONS } from "src/query/queryKeys";

// eslint-disable-next-line no-undef
const apiUrl = `${process.env.REACT_APP_API_URL}`;
const CHAT_USERS_BASE_URL = `${apiUrl}/api/2.0/chat_users`;
const ASSETS_BASE_URL = `${apiUrl}/api/2.0/assets`;

// Sync action for invalidating live view (e.g. for reducer or components)
export const invalidateLiveViewData = createAction<void>(INVALIDATE_LIVE_VIEW_DATA);

// API functions
async function fetchChatUsersApi(params: {
  page: number;
  filter: string;
  global: string | boolean;
}) {
  const { page, filter, global } = params;
  const url = buildUrl(CHAT_USERS_BASE_URL, {
    path: "/all",
    queryParams: { page, filter, global: String(global) },
  });
  const { data } = await axios.get(url);
  return data;
}

async function fetchChatUserApi(id: string | number) {
  const { data } = await axios.get(`${CHAT_USERS_BASE_URL}/${id}`);
  return data;
}

async function fetchNudgesApi() {
  const url = buildUrl(CHAT_USERS_BASE_URL, { path: "/nudges" });
  const { data } = await axios.get(url);
  return data ?? [];
}

async function fetchLocationsChatUsersApi(params: {
  filter: string;
  global: string | boolean;
}) {
  const { filter, global } = params;
  const url = buildUrl(CHAT_USERS_BASE_URL, {
    path: "/locations",
    queryParams: { filter, global: String(global) },
  });
  const { data } = await axios.get(url);
  return data ?? [];
}

async function associateChatUserApi(params: {
  chatUserId: string | number;
  uuid: string;
}) {
  const { chatUserId, uuid } = params;
  const { data } = await axios.put(
    `${CHAT_USERS_BASE_URL}/${chatUserId}/associate`,
    null,
    { params: { uuid: uuid } }
  );
  return data;
}

async function sendMessageAsEmailApi(params: {
  chatUserId: string | number;
  message: string;
}) {
  const { chatUserId, message } = params;
  const { data } = await axios.put(
    `${CHAT_USERS_BASE_URL}/${chatUserId}/email`,
    null,
    { params: { message } }
  );
  return data;
}

async function updateUnreadForChatUserApi(uuid: string | string[]) {
  const uuidParam = typeof uuid === "string" ? uuid : (Array.isArray(uuid) ? uuid[0] : "");
  const { data } = await axios.put(`${CHAT_USERS_BASE_URL}/unread`, null, {
    params: { uuid: uuidParam, unread: false },
  });
  return data;
}

async function blockChatUserApi(params: {
  chatUserId: string | number;
  blocked: boolean;
}) {
  const { chatUserId, blocked } = params;
  const { data } = await axios.put(
    `${CHAT_USERS_BASE_URL}/${chatUserId}/block`,
    null,
    { params: { blocked } }
  );
  return data;
}

async function fetchCurrentlyViewingAssetApi(chatUserId: string | number) {
  const { data: analyticsData } = await axios.get(
    `${CHAT_USERS_BASE_URL}/${chatUserId}/analytics`
  );
  
  // If assetId exists, fetch asset details
  if (analyticsData?.assetId) {
    const { data: assetData } = await axios.get(
      `${ASSETS_BASE_URL}/${analyticsData.assetId}`
    );
    
    // Merge asset data into the response
    return {
      ...analyticsData,
      asset: assetData,
    };
  }
  
  return analyticsData;
}

// Query key factories
export const chatUsersKeys = {
  all: [CHAT_USERS] as const,
  list: (params: { page: number; filter: string; global: string | boolean }) =>
    [CHAT_USERS, FETCH, params] as const,
  detail: (id: string | number) => [CHAT_USER, id] as const,
  currentlyViewingAsset: (chatUserId: string | number) =>
    [CHAT_USER, chatUserId, "currentlyViewingAsset"] as const,
  nudges: () => [CHAT_USER_NUDGES, FETCH] as const,
  locations: (params: { filter: string; global: string | boolean }) =>
    [CHAT_USER_LOCATIONS, FETCH, params] as const,
};

// Hooks

export function useChatUsersQuery(params: {
  page: number;
  filter: string;
  global: string | boolean;
}) {
  return useQuery({
    queryKey: chatUsersKeys.list(params),
    queryFn: () => fetchChatUsersApi(params),
  });
}

export function useChatUsersInfiniteQuery(params: {
  filter: string;
  global: string | boolean;
}) {
  return useInfiniteQuery({
    queryKey: [...chatUsersKeys.all, "infinite", params],
    queryFn: ({ pageParam = 0 }) =>
      fetchChatUsersApi({ ...params, page: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: {
      currentPage?: number;
      totalPages?: number;
    }) => {
      const current = lastPage?.currentPage ?? 0;
      const total = lastPage?.totalPages ?? 0;
      return current < total - 1 ? current + 1 : undefined;
    },
  });
}

export function useChatUserQuery(id: string | number | null) {
  return useQuery({
    queryKey: chatUsersKeys.detail(id!),
    queryFn: () => fetchChatUserApi(id!),
    enabled: id != null,
  });
}

export function useNudgesQuery() {
  return useQuery({
    queryKey: chatUsersKeys.nudges(),
    queryFn: fetchNudgesApi,
  });
}

export function useChatUserLocationsQuery(params: {
  filter: string;
  global: string | boolean;
}) {
  return useQuery({
    queryKey: chatUsersKeys.locations(params),
    queryFn: () => fetchLocationsChatUsersApi(params),
  });
}

export function useAssociateChatUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: associateChatUserApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatUsersKeys.all });
    },
  });
}

export function useSendMessageAsEmailMutation() {
  return useMutation({
    mutationFn: sendMessageAsEmailApi,
  });
}

export function useUpdateUnreadForChatUserMutation() {
  return useMutation({
    mutationFn: updateUnreadForChatUserApi,
  });
}

export function useBlockChatUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: blockChatUserApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatUsersKeys.all });
    },
  });
}

export function useFetchCurrentlyViewingAssetMutation() {
  return useMutation({
    mutationFn: fetchCurrentlyViewingAssetApi,
  });
}

/** Caches response from fetchCurrentlyViewingAssetApi in React Query. Use in component to read/display currently viewing asset for a chat user. */
export function useCurrentlyViewingAssetQuery(chatUserId: string | number | null | undefined) {
  return useQuery({
    queryKey: chatUserId != null ? chatUsersKeys.currentlyViewingAsset(chatUserId) : [CHAT_USER, "currentlyViewingAsset", null],
    queryFn: () => fetchCurrentlyViewingAssetApi(chatUserId!),
    enabled: chatUserId != null,
  });
}

// Legacy-style function APIs for components that still dispatch or call (e.g. passing to legacy code).
// These call the API and return the promise; they do not update React Query cache.
export const chatUsersApi = {
  fetchChatUsers: fetchChatUsersApi,
  fetchChatUser: fetchChatUserApi,
  fetchNudges: fetchNudgesApi,
  fetchLocations: fetchLocationsChatUsersApi,
  associateChatUser: associateChatUserApi,
  sendMessageAsEmail: sendMessageAsEmailApi,
  updateUnreadForChatUser: updateUnreadForChatUserApi,
  blockChatUser: blockChatUserApi,
  fetchCurrentlyViewingAsset: fetchCurrentlyViewingAssetApi,
};
